import { useEffect, useMemo, useState } from 'react';
import { getApiBase } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';
import {
  createReportPdf, preloadPdfLogo, drawSectionBanner, drawKpiTiles, drawCallout,
  drawTable, drawEmptyState, drawClosingFooter, finalizeFooters, savePdf,
  fmtGeneratedAt, severityCell, COLORS,
  type DocCtx, type KpiTile, type TableCell,
} from '../lib/pdfTemplate';
import { fmtTs, getTz } from '../lib/globalTz';

type Lookback = '24h' | '7d' | '30d' | '60d' | '90d';
type QualityLevel = 'Excellent' | 'Good' | 'Needs Tuning' | 'Poor';
type RuleRisk = 'Low' | 'Medium' | 'High' | 'Critical';

interface DetectionRuleQuality {
  id: string;
  ruleName: string;
  source: string;
  alertVolume: number;
  incidents: number;
  truePositiveRate: number | null;
  falsePositiveRate: number | null;
  reopenedIncidents: number;
  averageTimeToCloseHours: number | null;
  suppressionFrequency: number;
  entityCoverage: number;
  mitreCoverage: 'Mapped' | 'Partial' | 'Missing';
  lastTriggeredDate: string | null;
  noisyEntities: string[];
  healthFailures: number;
  auditChanges: number;
  qualityScore: number;
  risk: RuleRisk;
  tuningRecommendation: string;
  evidence: string[];
}
interface QueryTable { id: string; title: string; columns: string[]; rows: Record<string, unknown>[]; warnings: string[]; skipped?: boolean; }
interface DetectionPayload {
  ok: true;
  customerId: string;
  customerName: string;
  lookback: Lookback;
  generatedAt: string;
  expiresAt: string | null;
  cacheHit?: boolean;
  score: { value: number; level: QualityLevel; summary: string };
  cards: Array<{ id: string; label: string; value: string | number; status: RuleRisk | 'Healthy'; helper: string }>;
  rules: DetectionRuleQuality[];
  healthFailures: Record<string, unknown>[];
  auditChanges: Record<string, unknown>[];
  recommendations: Array<{ priority: RuleRisk; ruleName: string; title: string; details: string; owner: string }>;
  tables: QueryTable[];
  diagnostics: string[];
}
interface Props { customerId?: string; customerName?: string; }

function text(value: unknown, max = 180): string {
  if (value === null || value === undefined) return '';
  const raw = Array.isArray(value) ? value.join(', ') : typeof value === 'string' ? value : JSON.stringify(value);
  const clean = raw.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}
function riskClass(risk: RuleRisk | 'Healthy'): string {
  if (risk === 'Critical') return 'bg-red-600 text-white border-red-400/40';
  if (risk === 'High') return 'bg-orange-500 text-white border-orange-300/40';
  if (risk === 'Medium') return 'bg-amber-500 text-slate-950 border-amber-300/40';
  if (risk === 'Low') return 'bg-sky-500 text-white border-sky-300/40';
  return 'bg-emerald-500 text-white border-emerald-300/40';
}
function scoreGradient(score: number): string {
  if (score < 50) return 'from-red-600 via-orange-500 to-amber-500';
  if (score < 70) return 'from-orange-500 via-amber-500 to-yellow-400';
  if (score < 85) return 'from-cyan-500 via-blue-500 to-indigo-500';
  return 'from-emerald-500 via-teal-500 to-cyan-500';
}
function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
function drawAiParagraphs(ctx: DocCtx, markdown: string): void {
  const { pdf } = ctx;
  const lines = markdown.split(/\r?\n/).slice(0, 120);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { ctx.y += 2; continue; }
    if (ctx.y > 270) { pdf.addPage(); ctx.y = 14; }
    if (line.startsWith('#')) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10.5);
      pdf.setTextColor(...COLORS.primaryDark);
      pdf.text(line.replace(/^#+\s*/, ''), 14, ctx.y + 4);
      ctx.y += 7;
      continue;
    }
    const bullet = /^(?:[-*]|\d+\.)\s+(.*)$/.exec(line);
    const printable = bullet ? `• ${bullet[1]}` : line.replace(/\*\*/g, '');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.3);
    pdf.setTextColor(...COLORS.ink);
    const wrapped = pdf.splitTextToSize(printable, 182);
    for (const w of wrapped) {
      if (ctx.y > 270) { pdf.addPage(); ctx.y = 14; }
      pdf.text(String(w), 14, ctx.y + 4);
      ctx.y += 4.2;
    }
  }
  ctx.y += 2;
}

async function exportPdf(payload: DetectionPayload, aiAnalysis: string | null, filenameBase: string): Promise<void> {
  await preloadPdfLogo();
  const tz = getTz();
  const ctx = createReportPdf({
    title: 'Detection Quality Score',
    customerName: payload.customerName,
    periodLabel: `${payload.lookback} lookback`,
    generatedLabel: fmtGeneratedAt(payload.generatedAt, tz),
  });

  drawSectionBanner(ctx, 'Detection Quality Overview', 'primary');
  const tiles: KpiTile[] = payload.cards.map((c) => ({
    value: String(c.value),
    label: c.label,
    tone: c.status === 'Critical' || c.status === 'High' ? 'danger'
      : c.status === 'Medium' ? 'warning'
      : c.status === 'Healthy' || c.status === 'Low' ? 'success'
      : 'default',
  }));
  drawKpiTiles(ctx, tiles);

  drawCallout(ctx, {
    tone: payload.score.value < 50 ? 'critical' : payload.score.value < 70 ? 'warning' : 'success',
    title: `Detection Quality: ${payload.score.value}/100 (${payload.score.level})`,
    body: payload.score.summary,
  });

  if (aiAnalysis) {
    drawSectionBanner(ctx, 'AI Analysis', 'primary');
    drawAiParagraphs(ctx, aiAnalysis.slice(0, 3500));
  }

  drawSectionBanner(ctx, 'Top Tuning Recommendations', 'orange');
  if (payload.recommendations.length) {
    const recRows: TableCell[][] = payload.recommendations.slice(0, 16).map((rec) => [
      severityCell(rec.priority),
      text(rec.ruleName, 40),
      text(`${rec.title}: ${rec.details}`, 150),
      text(rec.owner, 26),
    ]);
    drawTable(ctx, {
      head: ['Priority', 'Rule', 'Recommendation', 'Owner'],
      body: recRows,
      columnStyles: {
        0: { cellWidth: 18, halign: 'center' },
        1: { cellWidth: 38 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 26 },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No tuning recommendations for the selected lookback.');
  }

  drawSectionBanner(ctx, 'Lowest Quality Rules', 'primary');
  if (payload.rules.length) {
    const ruleRows: TableCell[][] = payload.rules.slice(0, 22).map((rule) => [
      String(rule.qualityScore),
      severityCell(rule.risk),
      text(rule.ruleName, 46),
      String(rule.alertVolume),
      rule.falsePositiveRate === null ? 'n/a' : `${rule.falsePositiveRate}%`,
      rule.mitreCoverage,
    ]);
    drawTable(ctx, {
      head: ['Score', 'Risk', 'Rule', 'Alerts', 'FP %', 'MITRE'],
      body: ruleRows,
      columnStyles: {
        0: { cellWidth: 14, halign: 'center' },
        1: { cellWidth: 18, halign: 'center' },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 16, halign: 'center' },
        4: { cellWidth: 16, halign: 'center' },
        5: { cellWidth: 22, halign: 'center' },
      },
      minRowHeight: 8,
    });
  } else {
    drawEmptyState(ctx, 'No scored rules for the selected lookback.');
  }

  drawSectionBanner(ctx, 'SentinelHealth / SentinelAudit Signals', 'orange');
  drawCallout(ctx, {
    tone: 'info',
    title: 'Analytic rule health',
    body: `Analytic run failures: ${payload.healthFailures.length}. Rule changes: ${payload.auditChanges.length}.`,
  });

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  savePdf(ctx, filenameBase);
}

export function DetectionQualityScoreTab({ customerId, customerName }: Props) {
  const { canCapability } = useAuth();
  const [lookback, setLookback] = useState<Lookback>('30d');
  const [payload, setPayload] = useState<DetectionPayload | null>(null);
  const [selectedTableId, setSelectedTableId] = useState('scored_rules');
  const [loading, setLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canRead = canCapability('detection_quality', 'read');
  const canRefresh = canCapability('detection_quality', 'refresh');
  const canAi = canCapability('detection_quality', 'ai_analysis');
  const canExport = canCapability('detection_quality', 'export');

  async function load(refresh = false) {
    if (!customerId) return;
    setLoading(true); setError(null);
    try {
      const resp = await fetch(`${getApiBase()}/detection-quality/summary?customerId=${encodeURIComponent(customerId)}&lookback=${encodeURIComponent(lookback)}${refresh ? '&refresh=true' : ''}`, { credentials: 'include' });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Detection Quality load failed (${resp.status})`);
      const data = parsed as DetectionPayload;
      setPayload(data); setAiAnalysis(null);
      setSelectedTableId(data.tables.find((t) => t.rows.length > 0)?.id ?? data.tables[0]?.id ?? 'scored_rules');
      toast({ title: data.cacheHit ? 'Detection Quality loaded from cache' : 'Detection Quality refreshed', description: data.score.summary });
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setLoading(false); }
  }
  useEffect(() => { if (customerId && canRead) void load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [customerId]);

  async function generateAiAnalysis() {
    if (!customerId || !payload) return;
    setAiBusy(true); setError(null);
    try {
      const resp = await fetch(`${getApiBase()}/detection-quality/ai-analysis`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, payload }) });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `AI analysis failed (${resp.status})`);
      setAiAnalysis(String(parsed.analysis ?? ''));
      toast({ title: parsed.fallback ? 'Fallback detection analysis generated' : 'Detection Quality AI analysis generated', description: parsed.model ?? 'AI analysis' });
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setAiBusy(false); }
  }
  async function exportData(format: 'pdf' | 'csv' | 'html') {
    if (!customerId || !payload) return;
    setExportBusy(format); setError(null);
    try {
      const resp = await fetch(`${getApiBase()}/detection-quality/export`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, payload, format, aiAnalysis }) });
      const parsed = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(parsed.message || parsed.error || `Export failed (${resp.status})`);
      const filenameBase = String(parsed.filenameBase || `kloudsecure-detection-quality-${customerId}`);
      if (format === 'csv') downloadText(`${filenameBase}.csv`, String(parsed.csv ?? ''), 'text/csv;charset=utf-8');
      if (format === 'html') downloadText(`${filenameBase}.html`, String(parsed.html ?? ''), 'text/html;charset=utf-8');
      if (format === 'pdf') await exportPdf(payload, aiAnalysis, filenameBase);
      toast({ title: `Detection Quality ${format.toUpperCase()} exported`, description: customerName ?? customerId });
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setExportBusy(null); }
  }

  const selectedTable = useMemo(() => payload?.tables.find((table) => table.id === selectedTableId) ?? payload?.tables[0] ?? null, [payload, selectedTableId]);
  const tableColumns = useMemo<FilterableColumnDef<Record<string, unknown>>[]>(() => {
    const columns = selectedTable?.columns?.length ? selectedTable.columns : ['message'];
    return columns.map((column) => ({ key: column, header: column, headerText: column, value: (row) => row[column], render: (row) => text(row[column], 320) }));
  }, [selectedTable]);
  const ruleColumns = useMemo<FilterableColumnDef<DetectionRuleQuality>[]>(() => [
    { key: 'qualityScore', header: 'Score', value: (r) => r.qualityScore, render: (r) => <span className="font-black text-lg">{r.qualityScore}</span> },
    { key: 'risk', header: 'Risk', value: (r) => r.risk, render: (r) => <span className={`rounded-full px-2 py-0.5 text-xs font-black ${riskClass(r.risk)}`}>{r.risk}</span> },
    { key: 'ruleName', header: 'Rule', value: (r) => r.ruleName, render: (r) => <div><div className="font-bold">{r.ruleName}</div><div className="text-xs text-muted-foreground">{r.source || 'Sentinel / Defender'}</div></div> },
    { key: 'alertVolume', header: 'Alerts', value: (r) => r.alertVolume },
    { key: 'truePositiveRate', header: 'TP %', value: (r) => r.truePositiveRate ?? '', render: (r) => r.truePositiveRate === null ? 'n/a' : `${r.truePositiveRate}%` },
    { key: 'falsePositiveRate', header: 'FP %', value: (r) => r.falsePositiveRate ?? '', render: (r) => r.falsePositiveRate === null ? 'n/a' : `${r.falsePositiveRate}%` },
    { key: 'reopenedIncidents', header: 'Reopened', value: (r) => r.reopenedIncidents },
    { key: 'averageTimeToCloseHours', header: 'Avg Close', value: (r) => r.averageTimeToCloseHours ?? '', render: (r) => r.averageTimeToCloseHours === null ? 'n/a' : `${r.averageTimeToCloseHours}h` },
    { key: 'suppressionFrequency', header: 'Suppressions', value: (r) => r.suppressionFrequency },
    { key: 'entityCoverage', header: 'Entities', value: (r) => r.entityCoverage },
    { key: 'mitreCoverage', header: 'MITRE', value: (r) => r.mitreCoverage, render: (r) => <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${r.mitreCoverage === 'Mapped' ? 'bg-emerald-500 text-white' : r.mitreCoverage === 'Partial' ? 'bg-amber-500 text-slate-950' : 'bg-red-600 text-white'}`}>{r.mitreCoverage}</span> },
    { key: 'lastTriggeredDate', header: 'Last Triggered', value: (r) => r.lastTriggeredDate ?? '', render: (r) => r.lastTriggeredDate ? fmtTs(r.lastTriggeredDate) : 'n/a' },
    { key: 'noisyEntities', header: 'Noisy Entities', value: (r) => r.noisyEntities.join(', '), render: (r) => text(r.noisyEntities.join(', '), 220) },
    { key: 'healthFailures', header: 'Health Failures', value: (r) => r.healthFailures },
    { key: 'auditChanges', header: 'Audit Changes', value: (r) => r.auditChanges },
    { key: 'tuningRecommendation', header: 'Recommendation', value: (r) => r.tuningRecommendation, render: (r) => text(r.tuningRecommendation, 360) },
  ], []);
  const healthColumns = useMemo<FilterableColumnDef<Record<string, unknown>>[]>(() => ['TimeGenerated', 'OperationName', 'Status', 'ResourceName', 'ResourceKind', 'Reason', 'Description'].map((column) => ({ key: column, header: column, value: (row) => row[column], render: (row) => text(row[column], 300) })), []);
  const auditColumns = useMemo<FilterableColumnDef<Record<string, unknown>>[]>(() => ['TimeGenerated', 'OperationName', 'Operation', 'ResourceName', 'ResourceKind', 'Caller', 'Status', 'Description'].map((column) => ({ key: column, header: column, value: (row) => row[column], render: (row) => text(row[column], 300) })), []);

  if (!canRead) return <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">Detection Quality Score is hidden by Permissions Master. Enable Detection Quality Score → Read for this role.</div>;

  return <div className="space-y-5">
    <div className="relative overflow-hidden rounded-3xl border border-violet-400/30 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 p-6 shadow-2xl">
      <div className="absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_top_right,rgba(168,85,247,0.30),transparent_42%)]" />
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.35em] text-violet-200/80 font-black">Detection Engineering Maturity</div>
          <h2 className="mt-1 text-3xl font-black text-white">Detection Quality Score</h2>
          <p className="mt-2 max-w-5xl text-sm text-violet-100/80">Scores analytic rules using alert volume, TP/FP classification, reopened incidents, closure time, suppression signals, entity coverage, MITRE coverage, SentinelHealth failures and SentinelAudit rule changes.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div><label className="text-xs font-semibold text-violet-100/80">Lookback</label><select value={lookback} onChange={(e) => setLookback(e.target.value as Lookback)} className="mt-1 rounded-xl border border-white/20 bg-slate-950/80 px-3 py-2 text-sm text-white"><option value="24h">24 hours</option><option value="7d">7 days</option><option value="30d">30 days</option><option value="60d">60 days</option><option value="90d">90 days</option></select></div>
          <button type="button" onClick={() => void load(false)} disabled={loading || !customerId} className="rounded-xl bg-blue-500 px-4 py-2 text-sm font-bold text-white hover:bg-blue-400 disabled:opacity-60">{loading ? 'Loading…' : 'Load'}</button>
          {canRefresh && <button type="button" onClick={() => void load(true)} disabled={loading || !customerId} className="rounded-xl border border-white/20 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60">Refresh</button>}
          {canAi && payload && <button type="button" onClick={() => void generateAiAnalysis()} disabled={aiBusy} className="rounded-xl border border-fuchsia-300/50 bg-fuchsia-500/20 px-4 py-2 text-sm font-bold text-fuchsia-50 hover:bg-fuchsia-500/30 disabled:opacity-60">{aiBusy ? 'Analyzing…' : 'AI Analysis'}</button>}
          {canExport && payload && <button type="button" onClick={() => void exportData('pdf')} disabled={!!exportBusy} className="rounded-xl border border-white/20 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60">Export PDF</button>}
          {canExport && payload && <button type="button" onClick={() => void exportData('csv')} disabled={!!exportBusy} className="rounded-xl border border-white/20 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60">Export CSV</button>}
          {canExport && payload && <button type="button" onClick={() => void exportData('html')} disabled={!!exportBusy} className="rounded-xl border border-white/20 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60">Export HTML</button>}
        </div>
      </div>
      {payload && <p className="relative mt-4 text-xs text-violet-100/80">Generated {fmtTs(payload.generatedAt)} · {payload.cacheHit ? 'Loaded from DB cache' : 'Freshly queried'} · Cache expires {payload.expiresAt ? fmtTs(payload.expiresAt) : 'n/a'}</p>}
    </div>

    {!customerId && <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">Select a customer tenant to calculate Detection Quality Score.</div>}
    {error && <div className="rounded-2xl border border-red-400 bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900">{error}</div>}

    {payload && <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className={`rounded-2xl bg-gradient-to-br ${scoreGradient(payload.score.value)} p-5 text-white shadow-xl`}><div className="text-xs font-black uppercase">Detection Quality</div><div className="mt-2 text-5xl font-black">{payload.score.value}<span className="text-2xl">/100</span></div><div className="font-bold">{payload.score.level}</div><p className="mt-3 text-sm text-white/90">{payload.score.summary}</p></div>
        {payload.cards.filter((card) => card.id !== 'score').map((card) => <div key={card.id} className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="text-xs font-black uppercase tracking-wide text-muted-foreground">{card.label}</div><div className="mt-3 flex items-center justify-between gap-3"><div className="text-3xl font-black text-foreground">{card.value}</div><span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${riskClass(card.status)}`}>{card.status}</span></div><p className="mt-3 text-sm text-muted-foreground">{card.helper}</p></div>)}
      </div>

      {payload.diagnostics.length > 0 && <div className="rounded-2xl border border-amber-400/60 bg-amber-500/10 p-4 text-sm text-amber-100"><div className="font-black">Partial data warnings</div><ul className="mt-2 list-disc pl-5">{payload.diagnostics.map((d, i) => <li key={i}>{d}</li>)}</ul></div>}

      {aiAnalysis && <div className="rounded-2xl border border-fuchsia-400/50 bg-fuchsia-500/10 p-5 shadow-sm"><h3 className="text-lg font-black text-foreground">AI Detection Engineering Analysis</h3><pre className="mt-3 whitespace-pre-wrap text-sm leading-6 text-foreground/90">{aiAnalysis}</pre></div>}

      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="flex items-center justify-between gap-3"><div><h3 className="text-lg font-black text-foreground">Prioritized Detection Engineering Recommendations</h3><p className="text-sm text-muted-foreground">What to tune first, based on quality score, false positives, SentinelHealth and SentinelAudit context.</p></div><span className="rounded-full border border-border px-3 py-1 text-xs font-bold">{payload.recommendations.length} recommendation(s)</span></div><div className="mt-4 grid gap-3 lg:grid-cols-2">{payload.recommendations.map((rec, index) => <div key={`${rec.ruleName}-${index}`} className="rounded-xl border border-border bg-background/60 p-4"><div className="flex items-center justify-between gap-2"><div className="font-black">{rec.title}</div><span className={`rounded-full px-2 py-0.5 text-xs font-black ${riskClass(rec.priority)}`}>{rec.priority}</span></div><div className="mt-1 text-xs text-muted-foreground">Rule: {rec.ruleName} · Owner: {rec.owner}</div><p className="mt-2 text-sm text-foreground/90">{rec.details}</p></div>)}</div></div>

      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="flex items-center justify-between gap-3"><div><h3 className="text-lg font-black text-foreground">Rule-Level Detection Quality</h3><p className="text-sm text-muted-foreground">Use the column ... menu for filters, checklist values and autosize.</p></div><span className="rounded-full border border-border px-3 py-1 text-xs font-bold">{payload.rules.length} rule(s)</span></div><div className="mt-4"><FilterableColumnTable<DetectionRuleQuality> columns={ruleColumns} rows={payload.rules} storageKey={`detection-quality-rules-${customerId}`} rowKey={(row) => row.id} emptyMessage="No rules found for the selected lookback." maxHeightClassName="max-h-[620px]" /></div></div>

      <div className="grid gap-5 xl:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><h3 className="text-lg font-black text-foreground">SentinelHealth Analytic Run Failures</h3><p className="text-sm text-muted-foreground">Failed/unhealthy analytic-rule operations with reason details where available.</p><div className="mt-4"><FilterableColumnTable<Record<string, unknown>> columns={healthColumns} rows={payload.healthFailures} storageKey={`detection-quality-health-${customerId}`} rowKey={(row, index) => `${index}-${text(row.ResourceName)}`} emptyMessage="No SentinelHealth analytic failures found." maxHeightClassName="max-h-[520px]" /></div></div>
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><h3 className="text-lg font-black text-foreground">SentinelAudit Rule Changes</h3><p className="text-sm text-muted-foreground">Analytic rule create/update/delete/enable/disable activity for governance review.</p><div className="mt-4"><FilterableColumnTable<Record<string, unknown>> columns={auditColumns} rows={payload.auditChanges} storageKey={`detection-quality-audit-${customerId}`} rowKey={(row, index) => `${index}-${text(row.ResourceName)}`} emptyMessage="No SentinelAudit rule changes found." maxHeightClassName="max-h-[520px]" /></div></div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm"><div className="flex flex-wrap items-end justify-between gap-3"><div><h3 className="text-lg font-black text-foreground">Raw Detection Quality Evidence Tables</h3><p className="text-sm text-muted-foreground">Detailed KQL outputs used by the scoring engine. Use ... filters for drilldowns.</p></div><select value={selectedTableId} onChange={(event) => setSelectedTableId(event.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">{payload.tables.map((table) => <option key={table.id} value={table.id}>{table.title} ({table.rows.length})</option>)}</select></div>{selectedTable?.warnings?.length ? <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">{selectedTable.warnings.join(' ')}</div> : null}<div className="mt-4"><FilterableColumnTable<Record<string, unknown>> columns={tableColumns} rows={selectedTable?.rows ?? []} storageKey={`detection-quality-raw-${customerId}-${selectedTable?.id ?? 'none'}`} rowKey={(row, index) => `${selectedTable?.id ?? 'row'}-${index}-${text(row[selectedTable?.columns[0] ?? ''])}`} emptyMessage="No rows for this evidence table or current filters." maxHeightClassName="max-h-[560px]" /></div></div>
    </>}
  </div>;
}
